import { DateTime } from "luxon";
import { RequestDetailsResponse, RequestModel, SchoolModel } from "model";

import {
  getDefaultEndTime,
  getDefaultStartTime,
  getNextWeekday,
} from "~components/RequestForm/hooks";

type SubReqIntervals = RequestDetailsResponse["sub-req/time"];

type CopyRequestProps = {
  request: RequestDetailsResponse;
  school?: SchoolModel;
  now: DateTime;
};

/**
 * If the request's preferred subs were originally preferred as part of either
 * auto prefer or SH priority, don't copy over the preferred subs to the new request.
 */
const shouldNotCopySubsTags = ["SH_PRIORITY_PREFERRED_SUBS", "AUTO_PREFERRED_SUBS"];

/**
 * Transforms the copied request response so we can use it in the Request Form
 * See: https://swingeducation.atlassian.net/browse/PDE-4310
 * @param request The request response
 * @param date The current date/time
 * @returns The RequestModel
 */
export function copyRequest({ request, now, school }: CopyRequestProps) {
  const shouldCopyPreferredSubs = !shouldNotCopySubsTags.some((item) =>
    request?.["sub-req/tags"]?.includes(item),
  );

  // Copy over the following fields
  const newRequest = {
    // School
    "sub-req/school": request["sub-req/school"],
    // Time Interval
    "sub-req/time": request["sub-req/time"],
    // Long Term - Additional Edits
    "sub-req/multi-day-adjustments": request["sub-req/multi-day-adjustments"],
    // Teacher
    "sub-req/classroom-user": request["sub-req/classroom-user"],
    "sub-req/teacher": request["sub-req/teacher"],
    // Grade
    "sub-req/grade": request["sub-req/grade"],
    // Subject
    "sub-req/subject": request["sub-req/subject"],
    // Contact
    "sub-req/contact-name": request["sub-req/contact-name"],
    "sub-req/contact-number": request["sub-req/contact-number"],
    "sub-req/contact-email": request["sub-req/contact-email"],
    // Position
    "sub-req/position": request["sub-req/position"],
    // Budget
    "sub-req/budget": request["sub-req/budget"],
    // Internal Code
    "sub-req/invoice-memo": request["sub-req/invoice-memo"],
    // Credential / Permit Required
    "sub-req/credential-required?": request["sub-req/credential-required?"],
    // Lesson Plan Provided
    "sub-req/lesson-plan-provided?": request["sub-req/lesson-plan-provided?"],
    // Notes
    "sub-req/memo": request["sub-req/memo"],
    // Preferred Subs
    "sub-req/preferred-subs": shouldCopyPreferredSubs ? request["sub-req/preferred-subs"] : [],
  } satisfies Partial<RequestModel["request"]>;
  const sortedIntervals: SubReqIntervals = newRequest?.["sub-req/time"]?.sort((a, b) =>
    DateTime.fromISO(a["interval/start"]) < DateTime.fromISO(b["interval/start"]) ? -1 : 1,
  );

  // If the Request Start Date Time is greater 1 day ahead
  // START DATE IS IN THE FUTURE
  if (DateTime.fromISO(sortedIntervals[0]?.["interval/start"]) >= DateTime.local()) {
    // Copy all the Date Times (single days and multi days)
    return new RequestModel(newRequest);
  }

  const lastEndTime = DateTime.fromISO(sortedIntervals?.at(-1)?.["interval/end"] || now.toString());
  // If the endTime is on the same day as the new startTime, early return the RequestModel
  if (lastEndTime.day === now.day) {
    return new RequestModel(newRequest);
  }

  const nextWeekday = getNextWeekday(now);
  const intervals = [
    {
      ...newRequest["sub-req/time"]?.at(-1),
      "interval/start": getDefaultStartTime(nextWeekday, school?.hours),
      "interval/end": getDefaultEndTime(nextWeekday, school?.hours),
    },
  ];

  // LAST TIME IS IN THE PAST
  if (lastEndTime <= now) {
    /*
      Set the End Date to Today (turning it into a single day request)
      and the End Time as the default school’s End Time.
    */
    return new RequestModel({
      ...newRequest,
      "sub-req/time": intervals,
    });
  }

  const finalSubReqTimes = newRequest?.["sub-req/time"]?.reduce<SubReqIntervals>(
    (prev, current) => {
      // If the End Date Time in greater than NOW (this means its in the future)
      if (DateTime.fromISO(current["interval/end"]) >= now) {
        return [...prev, current];
      }
      return prev;
    },
    [],
  );

  const finalRequestObj = {
    ...newRequest,
    "sub-req/time": finalSubReqTimes,
  };

  return new RequestModel(finalRequestObj);
}
